import React, { useState, useEffect } from 'react';
import AppSidebar from '@/components/AppSidebar';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, RefreshCw, Activity, Download, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigitalTwinPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [twin, setTwin] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [assetRes, twinRes] = await Promise.all([
        axios.get(`${API}/assets/${id}`, config),
        axios.get(`${API}/digital-twin/${id}`, config)
      ]);

      setAsset(assetRes.data);
      setTwin(twinRes.data);
    } catch (error) {
      toast.error('Failed to load digital twin');
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/digital-twin/${id}/simulate`, {}, config);
      
      setSimulationResult(response.data);
      setShowResultModal(true);
      toast.success('Simulation completed successfully');
      
      await loadData();
    } catch (error) {
      toast.error('Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const downloadSimulationPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/digital-twin/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `digital_twin_simulation_${asset?.name}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Simulation report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="digital-twin-page">
      <AppSidebar />
      <div className="flex-1 min-w-0 overflow-y-auto h-screen p-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-black mb-6"
          onClick={() => navigate(`/asset/${id}`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Asset
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Digital Twin Simulation
            </h1>
            <p className="text-gray-600">{asset?.name}</p>
          </div>
          <Button
            onClick={runSimulation}
            disabled={simulating}
            className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
            data-testid="run-simulation-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${simulating ? 'animate-spin' : ''}`} />
            {simulating ? 'Running...' : 'Run Simulation'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Activity className="w-5 h-5 mr-2 text-red-500" />
                Voltage Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Predicted Voltage</p>
                <p className="text-2xl font-bold text-red-500">{twin?.predicted_voltage?.toFixed(2) || 0}V</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Actual Voltage</p>
                <p className="text-2xl font-bold text-black">{twin?.actual_voltage?.toFixed(2) || 0}V</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Error</p>
                <p className="text-lg font-semibold text-yellow-500">
                  {Math.abs(twin?.predicted_voltage - twin?.actual_voltage || 0).toFixed(2)}V
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Activity className="w-5 h-5 mr-2 text-red-500" />
                Temperature Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Predicted Temperature</p>
                <p className="text-2xl font-bold text-red-500">{twin?.predicted_temp?.toFixed(2) || 0}°C</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Actual Temperature</p>
                <p className="text-2xl font-bold text-black">{twin?.actual_temp?.toFixed(2) || 0}°C</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Error</p>
                <p className="text-lg font-semibold text-yellow-500">
                  {Math.abs(twin?.predicted_temp - twin?.actual_temp || 0).toFixed(2)}°C
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 md:col-span-2">
            <CardHeader>
              <CardTitle className="text-black">Model Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">State</p>
                  <p className="text-black font-medium capitalize">{twin?.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Warranty Degradation Rate</p>
                  <p className="text-black font-medium">{asset?.warranty_degradation_rate || 1.0}% /year</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Actual Degradation Rate</p>
                  <p className="text-black font-medium">{asset?.actual_degradation_rate || 1.0}% /year</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Last Calibration</p>
                  <p className="text-black font-medium">
                    {twin?.last_calibration ? new Date(twin.last_calibration).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Estimated EOL</p>
                  <p className="text-black font-medium">
                    {twin?.estimated_eol ? new Date(twin.estimated_eol).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 10-Year Degradation Projection */}
          <Card className="bg-white border-gray-200 md:col-span-2">
            <CardHeader>
              <CardTitle className="text-black">10-Year Degradation Projection</CardTitle>
            </CardHeader>
            <CardContent>
              {asset && (
                <svg 
                  key={`twin-graph-${asset.id}-${asset.warranty_degradation_rate}-${asset.actual_degradation_rate}`}
                  width="100%" 
                  height="300" 
                  viewBox="0 0 800 300" 
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Background grid */}
                  <defs>
                    <pattern id="twin-degradation-grid" width="80" height="30" patternUnits="userSpaceOnUse">
                      <path d="M 80 0 L 0 0 0 30" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="800" height="300" fill="url(#twin-degradation-grid)" />
                  
                  {/* Y-axis (SOH %) */}
                  <line x1="60" y1="20" x2="60" y2="260" stroke="#6b7280" strokeWidth="2" />
                  <text x="25" y="25" fill="#000" fontSize="12" fontWeight="bold">100%</text>
                  <text x="30" y="90" fill="#6b7280" fontSize="11">90%</text>
                  <text x="30" y="155" fill="#6b7280" fontSize="11">80%</text>
                  <text x="30" y="220" fill="#6b7280" fontSize="11">70%</text>
                  <text x="30" y="260" fill="#6b7280" fontSize="11">60%</text>
                  
                  {/* X-axis (Years) */}
                  <line x1="60" y1="260" x2="760" y2="260" stroke="#6b7280" strokeWidth="2" />
                  <text x="55" y="280" fill="#6b7280" fontSize="11">0</text>
                  <text x="195" y="280" fill="#6b7280" fontSize="11">2</text>
                  <text x="335" y="280" fill="#6b7280" fontSize="11">4</text>
                  <text x="475" y="280" fill="#6b7280" fontSize="11">6</text>
                  <text x="615" y="280" fill="#6b7280" fontSize="11">8</text>
                  <text x="745" y="280" fill="#6b7280" fontSize="11">10</text>
                  <text x="350" y="295" fill="#000" fontSize="12" fontWeight="bold">Years from Commissioning</text>
                  
                  {/* Warranty Degradation line (Green) */}
                  <polyline
                    points={(() => {
                      const degradationRate = asset.warranty_degradation_rate || 1.0;
                      const points = [];
                      for (let year = 0; year <= 10; year++) {
                        const x = 60 + (year * 70);
                        const soh = Math.max(60, 100 - (year * degradationRate));
                        const y = 260 - ((soh - 60) * 6);
                        points.push(`${x},${y}`);
                      }
                      return points.join(' ');
                    })()}
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth="3"
                  />
                  
                  {/* Actual Degradation line (Orange/Red, dashed) */}
                  <polyline
                    points={(() => {
                      const actualRate = asset.actual_degradation_rate || 1.0;
                      const points = [];
                      for (let year = 0; year <= 10; year++) {
                        const x = 60 + (year * 70);
                        const soh = Math.max(60, 100 - (year * actualRate));
                        const y = 260 - ((soh - 60) * 6);
                        points.push(`${x},${y}`);
                      }
                      return points.join(' ');
                    })()}
                    fill="none"
                    stroke="#f97316"
                    strokeWidth="3"
                    strokeDasharray="8 4"
                  />
                  
                  {/* Data points on warranty line */}
                  {(() => {
                    const degradationRate = asset.warranty_degradation_rate || 1.0;
                    const points = [];
                    for (let year = 0; year <= 10; year++) {
                      const x = 60 + (year * 70);
                      const soh = Math.max(60, 100 - (year * degradationRate));
                      const y = 260 - ((soh - 60) * 6);
                      points.push(
                        <circle key={`warranty-point-${year}`} cx={x} cy={y} r="4" fill="#dc2626" />
                      );
                    }
                    return points;
                  })()}
                  
                  {/* Data points on actual line */}
                  {(() => {
                    const actualRate = asset.actual_degradation_rate || 1.0;
                    const points = [];
                    for (let year = 0; year <= 10; year++) {
                      const x = 60 + (year * 70);
                      const soh = Math.max(60, 100 - (year * actualRate));
                      const y = 260 - ((soh - 60) * 6);
                      points.push(
                        <circle key={`actual-point-${year}`} cx={x} cy={y} r="4" fill="#f97316" />
                      );
                    }
                    return points;
                  })()}
                  
                  {/* Current position marker */}
                  {(() => {
                    if (!asset.commissioning_date) return null;
                    const commissionDate = new Date(asset.commissioning_date);
                    const now = new Date();
                    const yearsElapsed = Math.min(10, (now - commissionDate) / (365.25 * 24 * 60 * 60 * 1000));
                    const x = 60 + (yearsElapsed * 70);
                    const soh = Math.max(60, 100 - (yearsElapsed * (asset.warranty_degradation_rate || 1.0)));
                    const y = 260 - ((soh - 60) * 6);
                    return (
                      <>
                        <circle cx={x} cy={y} r="8" fill="#3b82f6" stroke="white" strokeWidth="3" />
                        <text x={x} y={y - 15} fill="#3b82f6" fontSize="12" fontWeight="bold" textAnchor="middle">Current</text>
                      </>
                    );
                  })()}
                  
                  {/* Legend */}
                  <rect x="580" y="30" width="200" height="100" fill="white" stroke="#6b7280" strokeWidth="1" />
                  <text x="590" y="50" fill="#000" fontSize="12" fontWeight="bold">Legend:</text>
                  
                  <line x1="590" y1="68" x2="630" y2="68" stroke="#dc2626" strokeWidth="3" />
                  <text x="640" y="72" fill="#6b7280" fontSize="11">Warranty ({asset.warranty_degradation_rate || 1.0}%/yr)</text>
                  
                  <line x1="590" y1="88" x2="630" y2="88" stroke="#f97316" strokeWidth="3" strokeDasharray="8 4" />
                  <text x="640" y="92" fill="#6b7280" fontSize="11">Actual ({asset.actual_degradation_rate || 1.0}%/yr)</text>
                  
                  <circle cx="610" cy="110" r="5" fill="#3b82f6" />
                  <text x="640" y="114" fill="#6b7280" fontSize="11">Current Position</text>
                </svg>
              )}
              <div className="text-sm text-gray-600 mt-4 text-center">
                Projected State of Health (SOH) over 10 years showing warranty vs actual degradation rates
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Simulation Results Modal */}
      <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-black flex items-center">
              <CheckCircle className="w-6 h-6 text-red-500 mr-2" />
              Simulation Results
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Digital twin simulation completed for {asset?.name}
            </DialogDescription>
          </DialogHeader>

          {simulationResult && (
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700 mb-1">Simulation Status</p>
                    <p className="text-lg font-semibold text-black">
                      {simulationResult.anomaly_detected ? (
                        <span className="flex items-center text-yellow-600">
                          <AlertTriangle className="w-5 h-5 mr-2" />
                          Anomaly Detected
                        </span>
                      ) : (
                        <span className="flex items-center text-red-600">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Normal Operation
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={downloadSimulationPDF}
                    className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>

              {/* Voltage Analysis */}
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black text-lg">Voltage Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Predicted</p>
                      <p className="text-xl font-bold text-red-600">
                        {simulationResult.simulation_result?.predicted_voltage?.toFixed(2)}V
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Actual</p>
                      <p className="text-xl font-bold text-black">
                        {simulationResult.simulation_result?.actual_voltage?.toFixed(2)}V
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Error</p>
                      <p className={`text-xl font-bold ${simulationResult.voltage_error_percent > 10 ? 'text-red-600' : 'text-yellow-600'}`}>
                        {simulationResult.voltage_error_percent}%
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-700">
                    Voltage deviation: {simulationResult.simulation_result?.voltage_error?.toFixed(2)}V
                  </div>
                </CardContent>
              </Card>

              {/* Temperature Analysis */}
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black text-lg">Temperature Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Predicted</p>
                      <p className="text-xl font-bold text-rose-600">
                        {simulationResult.simulation_result?.predicted_temp?.toFixed(2)}°C
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Actual</p>
                      <p className="text-xl font-bold text-black">
                        {simulationResult.simulation_result?.actual_temp?.toFixed(2)}°C
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Error</p>
                      <p className={`text-xl font-bold ${simulationResult.temp_error_celsius > 5 ? 'text-red-600' : 'text-yellow-600'}`}>
                        {simulationResult.temp_error_celsius.toFixed(2)}°C
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-700">
                    Temperature deviation: {simulationResult.simulation_result?.temp_error?.toFixed(2)}°C
                  </div>
                </CardContent>
              </Card>

              {/* Recommendations */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-black mb-2">Recommendations</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {simulationResult.anomaly_detected ? (
                    <>
                      <li>• Investigate voltage deviation - exceeds normal operating range</li>
                      <li>• Monitor temperature trends closely</li>
                      <li>• Schedule maintenance inspection within 24 hours</li>
                      <li>• Review BMS logs for anomaly patterns</li>
                    </>
                  ) : (
                    <>
                      <li>• System operating within normal parameters</li>
                      <li>• Continue regular monitoring schedule</li>
                      <li>• Next calibration due in 30 days</li>
                    </>
                  )}
                </ul>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowResultModal(false)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Close
                </Button>
                <Button
                  onClick={downloadSimulationPDF}
                  className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Full Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default DigitalTwinPage;
